import axios from 'axios';
import { getApiUrl } from '../utils/api-url';

const API_URL = getApiUrl();

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies/sessions
});

// Game API
export const gameAPI = {
  createGame: async (data: {
    name: string;
    universe: string;
    language: string;
    maxPlayers: number;
  }) => {
    // Attach the logged-in user's id so the game links to their account (auth is client-side).
    let userId: string | undefined;
    try { userId = JSON.parse(localStorage.getItem('userData') || 'null')?.user?.id; } catch { /* ignore */ }
    const response = await api.post('/api/games', { ...data, userId });
    return response.data;
  },

  // Backfill: link an already-created game to a user (for games made before auto-linking).
  claimGame: async (gameId: string, userId: string, role: 'creator' | 'player' = 'creator') => {
    const response = await api.post(`/api/games/${gameId}/claim`, { userId, role });
    return response.data;
  },

  // Creator-only: pause/resume and delete a game.
  pauseGame: async (gameId: string, userId: string, paused: boolean) => {
    const response = await api.post(`/api/games/${gameId}/pause`, { userId, paused });
    return response.data;
  },
  deleteGame: async (gameId: string, userId: string) => {
    const response = await api.delete(`/api/games/${gameId}`, { data: { userId } });
    return response.data;
  },

  getGame: async (id: string) => {
    const response = await api.get(`/api/games/${id}`);
    return response.data;
  },

  generateWorld: async (gameId: string) => {
    const response = await api.post(`/api/games/${gameId}/generate-world`);
    return response.data;
  },

  startGame: async (gameId: string) => {
    const response = await api.post(`/api/games/${gameId}/start`);
    return response.data;
  },

  joinGameByShareCode: async (shareCode: string) => {
    const response = await api.get(`/api/games/join/${shareCode}`);
    return response.data;
  },

  submitTurn: async (gameId: string, data: {
    playerId: string;
    actionType: string;
    actionInput: string;
  }) => {
    const response = await api.post(`/api/games/${gameId}/turns`, data);
    return response.data;
  },

  // One combined action for several co-located players (single browser) → one shared scene.
  submitScene: async (gameId: string, data: { playerIds: string[]; text: string }) => {
    const response = await api.post(`/api/games/${gameId}/turns/scene`, data);
    return response.data;
  },

  // Generate the opening scene right after the intro (idempotent; no-op if turns already exist).
  generateOpeningScene: async (gameId: string) => {
    const response = await api.post(`/api/games/${gameId}/turns/opening`, {});
    return response.data;
  },

  getGameState: async (gameId: string) => {
    const response = await api.get(`/api/games/${gameId}/turns/state`);
    return response.data;
  },

  getTurnHistory: async (gameId: string, limit: number = 50) => {
    const response = await api.get(`/api/games/${gameId}/turns?limit=${limit}`);
    return response.data;
  },

  getSessionGames: async () => {
    const response = await api.get('/api/games/session/current');
    return response.data;
  },

  getUserGames: async (userId: string) => {
    const response = await api.get(`/api/auth/user/${userId}/games`);
    return response.data;
  },
};

// Player API
export const playerAPI = {
  addPlayer: async (gameId: string, name: string) => {
    const response = await api.post(`/api/games/${gameId}/players`, { name });
    return response.data;
  },

  uploadAvatar: async (gameId: string, playerId: string, photos: File[]) => {
    const formData = new FormData();
    photos.forEach((photo) => {
      formData.append('photos', photo);
    });

    const response = await api.post(
      `/api/games/${gameId}/players/${playerId}/avatar`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  assignRoles: async (gameId: string) => {
    const response = await api.post(`/api/games/${gameId}/assign-roles`);
    return response.data;
  },

  deletePlayer: async (gameId: string, playerId: string) => {
    const response = await api.delete(`/api/games/${gameId}/players/${playerId}`);
    return response.data;
  },

  updatePlayer: async (gameId: string, playerId: string, name: string) => {
    const response = await api.patch(`/api/games/${gameId}/players/${playerId}`, { name });
    return response.data;
  },
};

// Auth API
export const authAPI = {
  requestOTP: async (phoneNumber: string) => {
    const response = await api.post('/api/auth/request-otp', { phoneNumber });
    return response.data;
  },

  verifyOTP: async (phoneNumber: string, code: string) => {
    const response = await api.post('/api/auth/verify-otp', { phoneNumber, code });
    return response.data;
  },

  linkGame: async (userId: string, gameId: string, role: string = 'creator') => {
    const response = await api.post('/api/auth/link-game', { userId, gameId, role });
    return response.data;
  },

  getUserGames: async (userId: string) => {
    const response = await api.get(`/api/auth/user/${userId}/games`);
    return response.data;
  },
};
